/**
 * Choosing a typeface. §6.3.
 *
 * A select with ninety-six entries in it is a list, not a choice. Type is
 * chosen by eye, so every row is set in its own face - and that is the whole
 * difficulty, because loading ninety-six fonts to draw a menu would cost more
 * than the design does.
 *
 * So a row's face is fetched when the row is scrolled into view and not before.
 * The preview uses the browser's own @font-face rather than the shaper: it is
 * one word of Latin in a menu, the browser can lay that out, and asking
 * HarfBuzz would mean compiling a face to draw a label.
 *
 * Arabic families are marked. On a card for a Doha print shop that is the first
 * thing a customer needs to know about a font, and it cannot be seen from the
 * name.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { availableFonts, fontFamilies } from '../text/fonts';

interface Props {
  value: string;
  onChange: ( family: string ) => void;
}

/** Faces already given to the browser for preview, so none is fetched twice. */
const previewed = new Set<string>();

export function FontField( { value, onChange }: Props ) {
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLDivElement>( null );

  const families = useMemo( () => fontFamilies(), [] );

  useEffect( () => {
    if ( ! open ) return;

    const away = ( event: MouseEvent ) => {
      if ( wrap.current && ! wrap.current.contains( event.target as Node ) ) setOpen( false );
    };

    const key = ( event: KeyboardEvent ) => {
      // Closes this and is marked handled, caught before the editor's
      // shortcuts, so Escape does not also deselect the text.
      if ( event.key !== 'Escape' ) return;

      event.preventDefault();
      setOpen( false );
    };

    window.addEventListener( 'mousedown', away );
    window.addEventListener( 'keydown', key, true );

    return () => {
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key, true );
    };
  }, [ open ] );

  return (
    <div className="tb-font" ref={ wrap }>
      <button
        type="button"
        className="tb-font__button"
        aria-expanded={ open }
        onClick={ () => setOpen( ! open ) }
      >
        <span className="tb-font__current" style={ { fontFamily: cssName( value ) } }>{ value }</span>
        <span className="tb-font__count">{ families.length }</span>
      </button>

      { open && (
        <div className="tb-font__pop" role="dialog" aria-label="Fonts">
          <FontList value={ value } onPick={ ( family ) => { onChange( family ); setOpen( false ); } } />
        </div>
      ) }
    </div>
  );
}

/**
 * The searchable list itself, for the dropdown above and for the font panel
 * the toolbar opens - one list, so the two cannot come to disagree about
 * which fonts there are or how they are shown.
 */
export function FontList( { value, onPick }: { value: string; onPick: ( family: string ) => void } ) {
  const [ search, setSearch ] = useState( '' );
  const families = useMemo( () => fontFamilies(), [] );

  const shown = useMemo( () => {
    const needle = search.trim().toLowerCase();

    return needle ? families.filter( ( f ) => f.toLowerCase().includes( needle ) ) : families;
  }, [ families, search ] );

  return (
    <>
      <input
        className="tb-input tb-font__search"
        placeholder={ `Search ${ families.length } fonts` }
        value={ search }
        autoFocus
        spellCheck={ false }
        onChange={ ( event ) => setSearch( event.target.value ) }
      />

      <div className="tb-font__list" role="listbox">
        { shown.length === 0 && <p className="tb-font__none">No font of that name.</p> }

        { shown.map( ( family ) => (
          <Row key={ family } family={ family } chosen={ family === value } onPick={ () => onPick( family ) } />
        ) ) }
      </div>
    </>
  );
}

function Row( {
  family,
  chosen,
  onPick,
}: {
  family: string;
  chosen: boolean;
  onPick: () => void;
} ) {
  const row = useRef<HTMLButtonElement>( null );
  const [ ready, setReady ] = useState( () => previewed.has( family ) );

  /*
   * The face arrives when the row does.
   *
   * Without this the menu would fetch every font the shop stocks the moment it
   * opened - about half a gigabyte on a real library - to draw labels the
   * customer has not scrolled to yet.
   */
  useEffect( () => {
    if ( ready || ! row.current ) return;

    const watcher = new IntersectionObserver(
      ( entries ) => {
        if ( ! entries.some( ( entry ) => entry.isIntersecting ) ) return;

        watcher.disconnect();
        void showFace( family ).then( () => setReady( true ) );
      },
      { rootMargin: '120px' }
    );

    watcher.observe( row.current );

    return () => watcher.disconnect();
  }, [ family, ready ] );

  const arabic = hasArabic( family );

  return (
    <button
      type="button"
      ref={ row }
      role="option"
      aria-selected={ chosen }
      className={ `tb-font__row${ chosen ? ' is-on' : '' }` }
      onClick={ onPick }
    >
      <span
        className="tb-font__name"
        style={ ready ? { fontFamily: cssName( family ) } : undefined }
      >
        { family }
      </span>

      { arabic && <span className="tb-font__tag" title="Has Arabic letters">ع</span> }
    </button>
  );
}

/* ---------------------------------------------------------------
 * Previews
 * ------------------------------------------------------------ */

/** Quoted, because plenty of these names have spaces and numbers in them. */
export const cssName = ( family: string ) => `"tbp ${ family }", system-ui, sans-serif`;

/**
 * Hands one family's regular face to the browser, for the label only.
 *
 * Named with a prefix so a preview can never be confused with a face the
 * document uses - what the card is drawn with comes from the shaper, always,
 * and nothing here is allowed to influence it.
 */
export async function showFace( family: string ): Promise<void> {
  if ( previewed.has( family ) ) return;

  previewed.add( family );

  const faces = availableFonts().filter( ( face ) => face.family === family );

  // The plainest face there is: upright, and nearest to regular.
  const upright = faces.filter( ( face ) => face.style === 'normal' );
  const pool = upright.length ? upright : faces;

  const best = pool.slice().sort(
    ( a, b ) => Math.abs( a.weight - 400 ) - Math.abs( b.weight - 400 )
  )[ 0 ];

  if ( ! best ) return;

  try {
    const face = new FontFace( `tbp ${ family }`, `url(${ best.url })` );

    await face.load();
    document.fonts.add( face );
  } catch {
    // A preview that will not load is not worth a word to the customer: the
    // name is still there, set in the interface font, and picking it still
    // works because the canvas loads its own copy through the shaper.
  }
}

/**
 * Whether a family covers Arabic.
 *
 * Guessed from the name rather than read from the file's character map. Reading
 * it would mean fetching every font to answer a question about a label, and the
 * families that carry Arabic are, in practice, the ones that say so.
 */
function hasArabic( family: string ): boolean {
  return /arab|cairo|tajawal|almarai|amiri|kufi|naskh|changa|messiri|readex|markazi|scheher|lateef|harmattan|mada|rubik|noto sans$|aref|reem|baloo bhaijaan/i
    .test( family );
}
